import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Package, Box } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ActionButton } from '@/shared/ui/action-button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';

import { useCreatePalletModal } from '../model/use-create-pallet-modal';
import { PALLET_TYPE_IDS, type PalletPortion, type PalletTypeId } from '@/entities/consignments';

export interface CreatePalletModalProps {
  isOpen: boolean;
  onClose: () => void;
  consignmentId: string;
  /** Selected parts of lines to put on the new pallets */
  selectedPortions?: PalletPortion[];
}

/**
 * The value stays the English unit name: it is what the API stores in
 * `inventoryUnit` and what the consignment lines carry, so only the visible
 * label is translated. Keys are legacy RITA's own inventory units.
 */
const getInventoryUnits = (t: TFunction) => [
  { value: 'Bulk (kg)', label: t('productLtu.28') },
  { value: 'Each', label: t('productLtu.29') },
  { value: 'Litre', label: t('productLtu.30') },
  { value: 'Pair', label: t('productLtu.31') },
  { value: 'Part', label: t('productLtu.32') }
];

export const CreatePalletModal = ({ isOpen, onClose, consignmentId, selectedPortions }: CreatePalletModalProps) => {
  const { t } = useTranslation();

  const {
    selectedLinesInfo,
    isLoading,
    isProcessing,
    error: hookError,
    handleSubmit,
    handleClose
  } = useCreatePalletModal({
    consignmentId,
    selectedPortions,
    onClose
  });

  // Local form state
  const [selectedType, setSelectedType] = useState<PalletTypeId | null>(null);
  const [inventoryUnitsPerTransport, setInventoryUnitsPerTransport] = useState<string>('');
  const [inventoryUnitType, setInventoryUnitType] = useState<string>('');
  const [transportUnitQuantity, setTransportUnitQuantity] = useState<string>('');
  const [validationError, setValidationError] = useState('');

  const hasSelectedLines = selectedLinesInfo.totalUnits > 0;
  const selectedLinesUnit = selectedLinesInfo.unit;
  const totalSelectedUnits = selectedLinesInfo.totalUnits;

  // Pre-select the unit if all selected lines share the same unit
  useEffect(() => {
    if (selectedLinesUnit && !inventoryUnitType) {
      setInventoryUnitType(selectedLinesUnit);
    }
  }, [selectedLinesUnit, inventoryUnitType]);

  const resetForm = () => {
    setSelectedType(null);
    setInventoryUnitsPerTransport('');
    setInventoryUnitType('');
    setTransportUnitQuantity('');
    setValidationError('');
  };

  const handleModalClose = () => {
    resetForm();
    handleClose();
  };

  const handleTypeSelect = (type: PalletTypeId) => {
    setSelectedType(type);
    setValidationError('');

    // If no lines selected, create empty pallet immediately
    if (!hasSelectedLines) {
      handleSubmit({
        type,
        inventoryUnitsPerTransport: 0,
        inventoryUnitType: '',
        transportUnitQuantity: 1
      });
      resetForm();
    }
  };

  const handleConfirm = () => {
    const invValue = parseInt(inventoryUnitsPerTransport, 10);
    const tuValue = parseInt(transportUnitQuantity, 10);

    if (!tuValue || tuValue <= 0) {
      setValidationError(t('pallet.create.error.invalidTransportUnits'));
      return;
    }
    if (!invValue || invValue <= 0) {
      setValidationError(t('pallet.create.error.invalidUnits'));
      return;
    }
    if (!inventoryUnitType) {
      setValidationError(t('pallet.create.error.noUnit'));
      return;
    }

    // Validate: total capacity must be >= total selected units
    const totalCapacity = tuValue * invValue;
    if (totalCapacity < totalSelectedUnits) {
      setValidationError(
        t('pallet.create.error.insufficientCapacity', {
          pallets: tuValue,
          perPallet: invValue,
          total: totalCapacity,
          needed: totalSelectedUnits
        })
      );
      return;
    }

    handleSubmit({
      type: selectedType!,
      inventoryUnitsPerTransport: invValue,
      inventoryUnitType,
      transportUnitQuantity: tuValue
    });
    resetForm();
  };

  const error = validationError || hookError;

  if (isLoading) {
    return (
      <Dialog open={isOpen} onOpenChange={handleModalClose}>
        <DialogContent className="sm:max-w-md">
          <div className="flex items-center justify-center py-8">
            <Spinner className="h-8 w-8" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleModalClose}>
      <DialogContent
        className="sm:max-w-md"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t('tracking.servicereq.addOverpacking.label')}</DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-6">
          {/* Mixed units warning */}
          {hasSelectedLines && selectedLinesUnit === null && (
            <Alert variant="destructive">
              <AlertDescription>{t('pallet.create.mixedUnitsWarning')}</AlertDescription>
            </Alert>
          )}

          {/* Step 1: Select type */}
          <div>
            <p className="text-sm text-muted-foreground mb-3">{t('pallet.create.selectType')}</p>
            <div className="grid grid-cols-2 gap-4">
              <Button
                variant="outline"
                className={`flex flex-col items-center gap-3 h-auto py-6 hover:border-primary hover:bg-primary/5 ${
                  selectedType === PALLET_TYPE_IDS.WAREHOUSE_PALLET ? 'border-primary bg-primary/5' : ''
                }`}
                onClick={() => handleTypeSelect(PALLET_TYPE_IDS.WAREHOUSE_PALLET)}>
                <Package className="h-8 w-8 text-muted-foreground" />
                <span className="text-sm font-medium">{t('overpackingType.1')}</span>
              </Button>

              <Button
                variant="outline"
                className={`flex flex-col items-center gap-3 h-auto py-6 hover:border-primary hover:bg-primary/5 ${
                  selectedType === PALLET_TYPE_IDS.WOODEN_CRATE ? 'border-primary bg-primary/5' : ''
                }`}
                onClick={() => handleTypeSelect(PALLET_TYPE_IDS.WOODEN_CRATE)}>
                <Box className="h-8 w-8 text-muted-foreground" />
                <span className="text-sm font-medium">{t('overpackingType.3')}</span>
              </Button>
            </div>
          </div>

          {/* Step 2: Transport unit quantity + inventory unit (only when lines are selected and same unit) */}
          {hasSelectedLines && selectedType && selectedLinesUnit !== null && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="transportUnitQuantity">{t('tracking.servicereq.overpackingqty.label')}</Label>
                <Input
                  id="transportUnitQuantity"
                  type="number"
                  min="1"
                  max="50"
                  value={transportUnitQuantity}
                  onChange={(e) => {
                    setTransportUnitQuantity(e.target.value);
                    setValidationError('');
                  }}
                  placeholder={t('pallet.create.transportUnitPlaceholder')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="inventoryUnitsPerTransport">{t('warehouse.pallet.iuqty.message')}</Label>
                <div className="flex gap-2">
                  <Input
                    id="inventoryUnitsPerTransport"
                    type="number"
                    min="1"
                    value={inventoryUnitsPerTransport}
                    onChange={(e) => {
                      setInventoryUnitsPerTransport(e.target.value);
                      setValidationError('');
                    }}
                    placeholder={t('pallet.create.inventoryUnitsPlaceholder')}
                    className="flex-1"
                  />
                  <Select
                    value={inventoryUnitType}
                    onValueChange={(v) => {
                      setInventoryUnitType(v);
                      setValidationError('');
                    }}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder={t('productLtu.1')} />
                    </SelectTrigger>
                    <SelectContent>
                      {getInventoryUnits(t).map((unit) => (
                        <SelectItem key={unit.value} value={unit.value}>
                          {unit.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {error && <p className="text-sm text-destructive">{error}</p>}

              <div className="flex justify-end pt-2">
                <ActionButton
                  intent="primary"
                  onClick={handleConfirm}
                  disabled={
                    !inventoryUnitsPerTransport || !transportUnitQuantity || !inventoryUnitType || isProcessing
                  }>
                  {isProcessing ? t('common.processing') : t('pallet.create.confirm')}
                </ActionButton>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
